import React, { useState } from 'react';
import { getImageHandlerSSR } from './utility';

interface DynamicImageProps {
  builderMode?: boolean;
  imageUrl: string;
  MediaMap?: Map<string, any>;
  altText?: string;
  placeholderImageStyle?: React.CSSProperties;
  style?: React.CSSProperties;
  onLoad?: () => void;
  onError?: () => void;
  [key: string]: any;
  lazyLoad?: boolean;
  ImageComponent?: any;
}

const Image: React.FC<DynamicImageProps> = ({
  builderMode = false,
  imageUrl,
  MediaMap,
  altText = '',
  placeholderImageStyle = {},
  style = {},
  width,
  height,
  onLoad,
  onError,
  lazyLoad = true,
  ImageComponent,
  ...props
}) => {
  const { currentSrc, srcset, sizes, fallbackUrl } = getImageHandlerSSR({
    builderMode,
    imageUrl,
    MediaMap,
  });

  const [isLoaded, setIsLoaded] = useState(false);
  const [hasClientRendered, setHasClientRendered] = useState(false);

  // Determine if we should use skeleton loader (needs explicit dimensions to work well)
  const hasDimensions =
    (!!width && !!height) || !!(style && (style.width || style.height || style.minHeight || style.aspectRatio));

  // Common styles for the smooth blur transition (client only)
  const baseTransition = 'opacity 0.5s ease-in-out, filter 0.5s ease-in-out';
  // Extract transition from style to apply to image directly
  const { transition, objectFit, ...wrapperStyles } = style;

  const customTransition = transition ? `, ${transition}` : '';

  // When JS is disabled, React effects never run and we never flip `hasClientRendered`
  // so we must NOT start from `opacity: 0` or images will stay invisible.
  const transitionStyles: React.CSSProperties =
    hasClientRendered && !builderMode
      ? {
          transition: baseTransition + customTransition,
          opacity: isLoaded ? 1 : 0,
          filter: isLoaded ? 'blur(0px)' : 'blur(20px)',
        }
      : {};

  const skeletonStyles: React.CSSProperties = {
    backgroundImage: fallbackUrl ? `url(${fallbackUrl})` : undefined,
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    position: 'relative',
    overflow: 'hidden',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    ...wrapperStyles,
  };

  const imgStyles = {
    width: '100%',
    height: '100%',
    objectFit: (objectFit || 'cover') as React.CSSProperties['objectFit'],
    borderRadius: 'inherit',
    ...style,
  };

  // Mark that we've reached the client so we can safely apply transitions.
  // With JS disabled this never runs, so SSR markup keeps default (visible) styles.
  React.useEffect(() => {
    setHasClientRendered(true);
  }, []);

  // Check for already loaded images (cached) to prevent them staying at opacity 0
  React.useEffect(() => {
    if (!hasClientRendered) return;
    // If ImageComponent (next/image) is used, it handles its own loading state usually,
    // but standard img tags need this check.
    const imgElements = document.querySelectorAll(`img[src="${currentSrc || imageUrl}"]`);
    imgElements.forEach((img: any) => {
      if (img.complete) {
        setTimeout(() => setIsLoaded(true), 100);
      }
    });
  }, [currentSrc, imageUrl, hasClientRendered]);

  // 1. Builder mode or simple URL (no MediaMap processing needed)
  if (builderMode || !MediaMap) {
    if (hasDimensions) {
      return (
        <div style={isLoaded ? wrapperStyles : skeletonStyles} className={props.className}>
          <img
            src={imageUrl}
            loading={lazyLoad ? 'lazy' : 'eager'}
            onLoad={(e) => {
              setTimeout(() => setIsLoaded(true), 100);
              onLoad && onLoad();
            }}
            alt={altText}
            style={{ ...imgStyles, ...transitionStyles }}
            {...props}
            className=""
            onContextMenu={(e) => e.preventDefault()}
          />
        </div>
      );
    }
    return (
      <img
        src={imageUrl}
        loading={lazyLoad ? 'lazy' : 'eager'}
        onLoad={(e) => {
          setTimeout(() => setIsLoaded(true), 100);
          onLoad && onLoad();
        }}
        alt={altText}
        style={{ ...style, ...transitionStyles }}
        {...props}
        onContextMenu={(e) => e.preventDefault()}
      />
    );
  }

  // 2. Next.js Optimization (if ImageComponent is provided)
  // We prefer this if available.
  if (ImageComponent) {
    // Determine the source to use. next/image usually wants the original URL or a processed one.
    // If MediaMap gave us a generic URL, usage that.
    const srcToUse = currentSrc || imageUrl;

    if (hasDimensions) {
      return (
        <div style={isLoaded ? wrapperStyles : skeletonStyles} className={props.className}>
          <ImageComponent
            src={srcToUse}
            alt={altText}
            width={width || 800}
            height={height || 600}
            loading={lazyLoad ? 'lazy' : 'eager'}
            onLoadingComplete={() => {
              setTimeout(() => setIsLoaded(true), 100);
              onLoad && onLoad();
            }}
            onError={() => {
              onError && onError();
            }}
            style={{
              ...imgStyles,
              ...transitionStyles,
            }}
            {...props}
            className=""
          />
        </div>
      );
    }
    return (
      <ImageComponent
        src={srcToUse}
        alt={altText}
        width={width || 800}
        height={height || 600}
        loading={lazyLoad ? 'lazy' : 'eager'}
        onLoadingComplete={() => {
          setTimeout(() => setIsLoaded(true), 100);
          onLoad && onLoad();
        }}
        onError={() => {
          onError && onError();
        }}
        style={{
          ...style,
          ...transitionStyles,
        }}
        {...props}
      />
    );
  }

  // 3. Fallback / Standard implementation (same as before but with added transition)

  // Note: Previous implementation had a "Loading placeholder" block.
  // We can simplify this by just rendering the main image with the blur effect.
  // The 'fallbackUrl' was used as a low-res placeholder in Gatsby.

  if (hasDimensions) {
    return (
      <div style={isLoaded ? wrapperStyles : skeletonStyles} className={props.className}>
        <img
          src={currentSrc}
          srcSet={srcset}
          sizes={sizes}
          alt={altText}
          loading={lazyLoad ? 'lazy' : 'eager'}
          onLoad={(e) => {
            setTimeout(() => setIsLoaded(true), 100);
            onLoad && onLoad();
          }}
          onError={(e) => {
            onError && onError();
          }}
          width={width}
          height={height}
          style={{
            ...imgStyles,
            ...transitionStyles,
          }}
          {...props}
          className=""
          onContextMenu={(e) => e.preventDefault()}
        />
      </div>
    );
  }
  return (
    <img
      src={currentSrc}
      srcSet={srcset}
      sizes={sizes}
      alt={altText}
      loading={lazyLoad ? 'lazy' : 'eager'}
      onLoad={(e) => {
        setTimeout(() => setIsLoaded(true), 100);
        onLoad && onLoad();
      }}
      onError={(e) => {
        onError && onError();
      }}
      width={width}
      height={height}
      style={{
        ...style,
        ...transitionStyles,
      }}
      {...props}
      onContextMenu={(e) => e.preventDefault()}
    />
  );
};

export default Image;
